import { Table, TableBody, TableCell, TableHead, TableRow } from "~/common/components/Table/TableElements";

import classes from "~/features/orders/orderDetails/components/ServiceCases/ServiceCasesTable.module.less";
import classNames from "classnames";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { LinkButton, LinkType } from "~/common/components/Link";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import LabelBox from "~/domains/salesCampaign/components/LabelBox/LabelBox";
import {
  type CaseDataObject,
  CaseIcon,
  CaseStatus,
} from "~/features/orders/orderDetails/components/ServiceCases/types";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import {
  addCommentByCode,
  fetchServiceCasesByLineNumber,
} from "~/features/orders/orderDetails/components/ServiceCases/api";
import AddCommentModal from "~/features/orders/orderDetails/components/ServiceCases/AddCommentModal";
import { t } from "i18next";
import { CreateCaseElement } from "~/features/orders/common/components/CreateCaseElement";
import { Alert } from "~/common/mui/Alert/Alert";
import { AlertTitle } from "~/common/mui";

interface ServiceCasesTableProps {
  openPopup: () => void;
  orderNumber?: string;
  serviceCase?: CaseDataObject[];
  lineNumber?: string;
  className?: string;
  userHasCreatedCase: boolean;
  setSnackbar: Dispatch<SetStateAction<{ open: boolean; message: string }>>;
  isOdd: boolean;
}

const LIST_SIZE = 3;

const ServiceCasesTable = ({
  serviceCase,
  orderNumber,
  userHasCreatedCase,
  lineNumber,
  openPopup,
  setSnackbar,
  isOdd,
  className,
}: ServiceCasesTableProps) => {
  const { val: displayMore, toggle: toggleShowMore } = useBooleanState(false);
  const [commentModalCode, setCommentModalCode] = useState<string | undefined>(undefined);
  const [caseData, setCaseData] = useState<CaseDataObject[] | undefined>(serviceCase);

  const isCaseProcessing = caseData?.some((item) => !item.completeData);

  const showButton = caseData && caseData?.filter((item) => item.completeData).length > 3;

  const onAddCommentAction = (code: string, message: string) => {
    addCommentByCode(code, message);
    setSnackbar({
      open: true,
      message: t("serviceCases.commentSent.snackbarInfo"),
    });
  };

  useEffect(() => {
    if (isCaseProcessing && orderNumber && lineNumber) {
      //Make call every 2 minutes
      const interval = setInterval(
        () => {
          fetchServiceCasesByLineNumber(orderNumber, lineNumber).then((res) => {
            setCaseData(res[lineNumber]);
          });
        },
        1000 * 60 * 2,
      );
      return () => clearInterval(interval);
    }
  }, [isCaseProcessing]);

  const serviceCaseItems = caseData
    ?.filter((item) => item.completeData)
    .slice(0, displayMore ? undefined : LIST_SIZE)
    .map((item) => (
      <TableRow className={isOdd ? classes.odd : classes.even}>
        <TableCell className={classes.caseNumber}>
          <LinkButton onClick={() => setCommentModalCode(item.code)} variant={LinkType.SECONDARY}>
            {item.sfCaseNumber}
          </LinkButton>
        </TableCell>
        <TableCell>
          <p>{item.reasonOfRequest}</p>
        </TableCell>
        <TableCell className={classes.statusBody}>
          <LabelBox variant="darkGrey" className={classes.statusLabelBox}>
            <Icon size={10} type={CaseIcon[item.status]} />
            {t(CaseStatus[item.status])}
          </LabelBox>

          <LinkButton onClick={() => setCommentModalCode(item.code)} iconBefore={<Icon type={IconType.CommentPlus} />}>
            <div className={classes.addCommentText}>{t("page.wishlist.comment.add")}</div>
          </LinkButton>
        </TableCell>
      </TableRow>
    ));

  const loadingBanner = (
    <TableCell colSpan={9}>
      <div className={classes.loadingContainer}>
        <CircleSpinner />
        <Alert className={classes.loadingMessage} severity="info">
          <AlertTitle>
            {userHasCreatedCase ? t("serviceCases.caseCreated.create") : t("serviceCases.caseLoading.processing")}
          </AlertTitle>
          {userHasCreatedCase ? t("serviceCases.caseCreated.label") : t("serviceCases.caseLoading.label")}
        </Alert>
      </div>
    </TableCell>
  );

  return (
    <>
      {commentModalCode && (
        <AddCommentModal
          onSend={onAddCommentAction}
          caseData={caseData?.find((item) => item.code === commentModalCode)}
          onClose={() => setCommentModalCode(undefined)}
        />
      )}
      <CreateCaseElement openPopup={openPopup} />

      <Table className={classNames(classes.table, className)}>
        {caseData && (
          <TableHead>
            <TableRow>
              <TableCell className={classes.header}>{t("addComment.caseNumber")}</TableCell>
              <TableCell className={classes.header}>{t("addComment.subject")}</TableCell>
              <TableCell className={classes.header}>{t("page.openorders.search.orderLineStatus")}</TableCell>
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {(isCaseProcessing || userHasCreatedCase) && loadingBanner}
          {serviceCaseItems}
        </TableBody>
        <TableRow>
          <TableCell colSpan={12}>
            <div className={classes.showMore}>
              {showButton && (
                <LinkButton
                  iconBefore={<Icon type={displayMore ? IconType.ChevronUp : IconType.ChevronDown} />}
                  onClick={toggleShowMore}
                >
                  {t(`searchPage.refinements.${displayMore ? "showLess" : "showMore"}`)}
                </LinkButton>
              )}
            </div>
          </TableCell>
        </TableRow>
      </Table>
    </>
  );
};

export default ServiceCasesTable;
